import { useTranslations } from '@vocab/react';
import {
  Button,
  Inline,
  TextField,
  Text,
  Stack,
  useToast,
  type FieldMessage,
} from 'braid-design-system';
import React, { type FormEvent } from 'react';

import { useMutateExpireJob } from 'src/hooks/useMutateExpireJob';

import translations from './.vocab';
import { validateExpiryDate } from './validateExpiryDate';

type FieldMessageProps = React.ComponentProps<typeof FieldMessage>;

interface FormState {
  expiryDate: string;
  expiryDateError?: string;
  expiryDateErrorTone: FieldMessageProps['tone'];
}

interface UpdateExpiryDateFormProps {
  getExpiryDateError: boolean;
  expiryDate: string;
  jobId: string;
  onClose(): void;
}

export const UpdateExpiryDateForm: React.FC<
  React.PropsWithChildren<UpdateExpiryDateFormProps>
> = ({ getExpiryDateError, expiryDate, jobId, onClose }) => {
  const showToast = useToast();
  const { t } = useTranslations(translations);

  const expiryDateError: string | undefined = getExpiryDateError
    ? t('...we had an issue getting the expiry date...')
    : undefined;

  const [formState, setFormState] = React.useState<FormState>({
    expiryDate,
    expiryDateError,
    expiryDateErrorTone: expiryDateError ? 'critical' : 'neutral',
  });
  const { loading, expireJob, error } = useMutateExpireJob({
    jobId,
  });

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const validationResult = validateExpiryDate(formState.expiryDate);

    if (!validationResult.valid) {
      setFormState((prevState) => ({
        ...prevState,
        expiryDateError: t(validationResult.errorMessage),
        expiryDateErrorTone: 'critical',
      }));
      return;
    }

    try {
      await expireJob(validationResult.expiredDate.toISOString());
      showToast({
        tone: 'positive',
        message: t('Expiry date ... has been successfully updated', {
          expiryDate: formState.expiryDate,
        }),
      });
      onClose();
    } catch {
      // use error from useMutation
    }
  };

  const onBlur = async () => {
    const validationResult = validateExpiryDate(formState.expiryDate);

    if (!validationResult.valid) {
      setFormState((prevState) => ({
        ...prevState,
        expiryDateError: t(validationResult.errorMessage),
        expiryDateErrorTone: 'critical',
      }));
      return;
    }

    setFormState((prevState) => ({
      ...prevState,
      expiryDateError: undefined,
      expiryDateErrorTone: 'neutral',
    }));
  };

  return (
    <form onSubmit={onSubmit}>
      <Stack space="small">
        <TextField
          id="expiry-date"
          label={t('(CS) Expire ad on')}
          placeholder={t('dd/MM/yyyy')}
          value={formState.expiryDate}
          tone={formState.expiryDateErrorTone}
          onChange={({ currentTarget }) =>
            setFormState((prevState) => ({
              ...prevState,
              expiryDate: currentTarget.value,
            }))
          }
          onBlur={onBlur}
          message={formState.expiryDateError}
          reserveMessageSpace
        />
        {error && (
          <Text tone="critical">
            {t('...we had an issue updating expiry date...')}
          </Text>
        )}
        <Inline space="small">
          <Button type="submit" loading={loading}>
            {loading ? t('Updating') : t('Update')} expiry date
          </Button>
        </Inline>
      </Stack>
    </form>
  );
};
